import { useEffect, useState } from "react";
import { Loader2, AlertTriangle, MessageCircle, CalendarPlus, CheckCircle2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { ResendConfirmAsk } from "@/components/ResendConfirmAsk";
import { HelpersPicker } from "@/components/HelpersPicker";

const CAL = {
  SYNCED: ["SINCRONIZZATO", "bg-[#61a229]/15 text-[#3f6d18]"],
  NOT_CONNECTED: ["NON COLLEGATO", "bg-amber-100 text-amber-800"],
  ERROR: ["ERRORE", "bg-red-100 text-red-700"],
  MOCKED: ["SIMULATO", "bg-amber-100 text-amber-800"],
};

export function ConfirmationActions({ job }) {
  if (!job?.whatsapp_link) return null;
  return (
    <div className="space-y-3" data-testid="confirmation-actions">
      <a href={job.whatsapp_link} target="_blank" rel="noreferrer" data-testid="whatsapp-link-button"
        className="flex items-center justify-center gap-2 rounded-full bg-[#25D366] px-4 py-2.5 text-sm font-semibold text-white hover:brightness-95 transition">
        <MessageCircle className="h-4 w-4" /> Invia conferma su WhatsApp
      </a>
      {job.calendar_event && (
        <div className="rounded-xl border bg-slate-50 p-3 text-xs text-slate-600" data-testid="calendar-event-info">
          <p className="flex items-center gap-1.5 font-semibold text-slate-800">
            <CalendarPlus className="h-4 w-4 text-[#61a229]" /> Evento Google Calendar
            <span className={`ml-auto rounded px-1.5 py-0.5 text-[10px] font-bold ${CAL[job.calendar_event.status]?.[1] || CAL.MOCKED[1]}`} data-testid="calendar-event-status">
              {CAL[job.calendar_event.status]?.[0] || job.calendar_event.status}
            </span>
          </p>
          {job.calendar_event.status === "SYNCED" ? (
            <p className="mt-1">Creato nel calendario di {job.calendar_event.calendar_id} · promemoria 30 min prima</p>
          ) : (
            <p className="mt-1">{job.calendar_event.status === "ERROR" ? "Sincronizzazione non riuscita." : "Il tecnico non ha ancora collegato Google Calendar."} Puoi aggiungerlo manualmente.</p>
          )}
          <a href={job.calendar_event.html_link} target="_blank" rel="noreferrer" className="mt-1 inline-block font-semibold text-[#61a229] hover:underline" data-testid="calendar-add-link">
            {job.calendar_event.status === "SYNCED" ? "Apri evento in Google Calendar" : "Aggiungi manualmente al calendario"}
          </a>
        </div>
      )}
    </div>
  );
}

export function AssignDialog({ job, open, onOpenChange, onDone }) {
  const [techs, setTechs] = useState([]);
  const [techId, setTechId] = useState("");
  const [when, setWhen] = useState({ date: "", start: "", end: "" });
  const [conflicts, setConflicts] = useState([]);
  const [blocked, setBlocked] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [note, setNote] = useState("");
  const [ask, setAsk] = useState(false);
  const [helpers, setHelpers] = useState([]);
  const teamOk = job && job.kind !== "cantiere" && !job.series_id;
  const changed = job && (when.date !== job.date || when.start !== job.start || when.end !== job.end);

  useEffect(() => {
    if (!open || !job) return;
    setDone(null);
    setNote("");
    setTechId(job.technician_id || "");
    setHelpers((job.helpers || []).map((h) => h.id));
    setWhen(job.proposal ? { date: job.proposal.date, start: job.proposal.start, end: job.proposal.end } : { date: job.date, start: job.start, end: job.end });
    api.get("/admin/technicians").then((r) => setTechs(r.data.filter((t) => t.active)));
  }, [open, job]);

  useEffect(() => {
    if (!techId || !when.date || !when.start || !when.end) return setConflicts([]);
    api.get("/admin/conflicts", { params: { technician_id: techId, ...when, job_id: job?.id } })
      .then((r) => setConflicts(r.data.conflicts)).catch(() => setConflicts([]));
  }, [techId, when, job]);

  const submit = async (notify_client = true) => {
    setAsk(false);
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/jobs/${job.id}/approve`, { technician_id: techId, ...when, client_note: note.trim() || null, notify_client,
        ...(teamOk ? { helper_ids: helpers.filter((h) => h !== techId) } : {}) });
      setDone(data);
      toast.success("Intervento confermato e assegnato");
      onDone?.(data);
    } catch (e) {
      if (e?.response?.status === 409) setBlocked(e.response.data.detail);
      else toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const propose = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/jobs/${job.id}/propose`, { ...when, client_note: note.trim() || null });
      setDone({ ...data, whatsapp_link: data.proposal.whatsapp_link, calendar_event: null, _proposal: true });
      toast.success("Proposta salvata: inviala al cliente su WhatsApp");
      onDone?.(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-lg" data-testid="assign-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">{done ? (done._proposal ? "Proposta pronta" : "Intervento confermato") : job?.status === "sospeso" ? "Riprogramma completamento" : "Approva e assegna tecnico"}</DialogTitle>
            <DialogDescription>{job?.client.first_name} {job?.client.last_name} · {job?.client.city}</DialogDescription>
          </DialogHeader>
          {done ? (
            <div className="space-y-4">
              <p className="flex items-center gap-2 text-sm text-[#3f6d18]"><CheckCircle2 className="h-4 w-4" /> Assegnato a {done.technician_name}</p>
              <ConfirmationActions job={done} />
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Tecnico</Label>
                <Select value={techId} onValueChange={setTechId}>
                  <SelectTrigger data-testid="assign-tech-select"><SelectValue placeholder="Seleziona tecnico" /></SelectTrigger>
                  <SelectContent>
                    {techs.map((t) => <SelectItem key={t.id} value={t.id} data-testid={`assign-tech-option-${t.id}`}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {teamOk && techId && <HelpersPicker techs={techs} leadId={techId} value={helpers} onChange={setHelpers} />}
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-2 col-span-3 sm:col-span-1"><Label>Data</Label>
                  <Input type="date" value={when.date} onChange={(e) => setWhen((w) => ({ ...w, date: e.target.value }))} data-testid="assign-date-input" /></div>
                <div className="space-y-2"><Label>Dalle</Label>
                  <Input type="time" value={when.start} onChange={(e) => setWhen((w) => ({ ...w, start: e.target.value }))} data-testid="assign-start-input" /></div>
                <div className="space-y-2"><Label>Alle</Label>
                  <Input type="time" value={when.end} onChange={(e) => setWhen((w) => ({ ...w, end: e.target.value }))} data-testid="assign-end-input" /></div>
              </div>
              {conflicts.length > 0 && (
                <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700" data-testid="assign-conflict-alert">
                  <p className="flex items-center gap-2 font-semibold"><AlertTriangle className="h-4 w-4" /> Conflitto di calendario</p>
                  <ul className="mt-1 list-disc pl-6">{conflicts.map((c, i) => <li key={i}>{c.message}</li>)}</ul>
                  <p className="mt-2 text-xs">Scegli un altro tecnico oppure inserisci manualmente una data/ora alternativa: il cliente riceverà la proposta su WhatsApp.</p>
                </div>
              )}
              {changed && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" data-testid="assign-reschedule-info">
                  Data alternativa rispetto alla richiesta del cliente ({job.date} {job.start}–{job.end}). Puoi confermare subito (il cliente riceverà la nuova data su WhatsApp) oppure solo proporla e attendere la sua risposta.
                </div>
              )}
              <div className="space-y-2">
                <Label>Messaggio aggiuntivo per il cliente (facoltativo)</Label>
                <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Es. Il tecnico la chiamerà 30 minuti prima dell'arrivo." data-testid="assign-client-note" />
              </div>
            </div>
          )}
          <DialogFooter>
            {done ? (
              <Button onClick={() => onOpenChange(false)} data-testid="assign-close-button">Chiudi</Button>
            ) : (
              <>
                {changed && job?.status === "in_attesa" && (
                  <Button variant="outline" onClick={propose} disabled={busy} data-testid="assign-propose-button" className="rounded-full">
                    Proponi al cliente senza confermare
                  </Button>
                )}
                <Button onClick={() => (job?.status === "confermato" ? setAsk(true) : submit())} disabled={!techId || busy} data-testid="assign-confirm-button" className="rounded-full">
                  {busy && <Loader2 className="h-4 w-4 animate-spin" />} Approva e assegna
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ResendConfirmAsk open={ask} onCancel={() => setAsk(false)} onAnswer={submit} external={techs.find((t) => t.id === techId)?.employment === "autonomo"} />
      <AlertDialog open={!!blocked} onOpenChange={(o) => !o && setBlocked(null)}>
        <AlertDialogContent data-testid="conflict-block-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-700"><AlertTriangle className="h-5 w-5" /> Assegnazione bloccata</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                <p>{blocked?.message}</p>
                <ul className="list-disc pl-5">{blocked?.conflicts?.map((c, i) => <li key={i}>{c.message}</li>)}</ul>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter><AlertDialogAction data-testid="conflict-block-ok-button">Ho capito</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
